/**
 * CustomerChatComposer — composer do portal parceiro (Story 4.1.5).
 *
 * Render (redesenhado em 06/08/2026 para o celular): DOIS botões só —
 * `[+] [ caixa de texto ] [🎤|➤]`. O `+` abre o menu de anexos (foto/arquivo e
 * gravar vídeo); o da direita alterna entre microfone e enviar conforme haja
 * texto. É o desenho do WhatsApp, e a razão é espaço: com anexo + vídeo +
 * microfone + enviar todos na barra, a caixa ficava com 146px numa tela de
 * 412px (35%). Agora tem 242px (59%) — medido no navegador.
 *
 * Comportamento:
 * - Enter envia (Shift+Enter quebra linha)
 * - Trim — ignora envios vazios
 * - A caixa cresce com o texto até 128px e então rola por dentro
 *   (`alturaAutomatica`); um `<textarea rows={1}>` não faz isso sozinho.
 * - Anexo vai para STORAGE (≤25 MB) e a mensagem carrega só a `storage_key`.
 *   Era base64 inline ≤1 MB até 06/08/2026 — ver `sendAttachment`.
 * - `disabled` desativa a caixa e os botões
 *
 * sender_type/sender_id são fixados no backend (PARTNER + current_user.id).
 * O JSONB de content segue a convenção:
 *   { type: "text", text: <string> }
 *   { type: "file"|"image", filename, mime, size, storage_key }
 *   (mensagens ANTIGAS ainda trazem `attachment_b64`; a bolha lê os dois)
 */

import { Suspense, useEffect, useRef, useState } from "react";
import { Loader2, Mic, Paperclip, Plus, Send, Video as VideoIcon } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ImageMarkupEditor } from "@/components/image-editor";
// Nota de voz (A4): reusa F1 (gravador) + o modal de consent + o upload portal.
import { VoiceRecorder, type VoiceRecorderPayload } from "@/components/common/VoiceRecorder";
// Vídeo curto GRAVADO no app (06/08/2026) — ver o docstring do useVideoRecorder
// para os números que justificam gravar em vez de aceitar da galeria.
import { VideoRecorder, type VideoRecorderPayload } from "@/components/common/VideoRecorder";
import { VoiceConsentModal } from "@/components/portal/VoiceConsentModal";
// Prévia de link: o cartão do site em cima do campo, com o ✕ para tirar (origem `portal`).
import { BarraDoCartao } from "@/components/links/BarraDoCartao";
import { useColagemDeArquivos } from "@/hooks/useColagemDeArquivos";
import { useFocoNaCaixa } from "@/hooks/useFocoNaCaixa";
import { linkDispensadoParaEnviar, primeiroLink } from "@/lib/links/links";
import { portalAtendimentosApi } from "@/services/portalAtendimentosApi";
import {
  useGrantVoiceConsent,
  useSendPortalMessage,
  useVoiceConsentStatus,
} from "@/hooks/usePortalAtendimento";

// 25 MB — teto do endpoint `/portal/atendimentos/attachment-upload`, conferido
// TAMBÉM no servidor (o cliente pode ser contornado). O antigo limite de 1 MB
// era do JSONB, quando o anexo viajava embutido na mensagem em base64.
const MAX_ANEXO_BYTES = 25 * 1024 * 1024;

/** Prazo máximo da compressão antes de desistir e enviar o original.
 *
 * 15s cobre foto grande em celular modesto com folga. O que ele impede é o
 * travamento silencioso: a decodificação por canvas pode simplesmente nunca
 * completar, e aí o envio ficaria pendurado sem erro. */
const TEMPO_MAX_COMPRESSAO_MS = 15_000;

/** Piso e teto da caixa de texto, em pixels. */
export const ALTURA_MIN_CAIXA = 48;
/** 128px ≈ 4 linhas. Passou disso, rola por dentro: uma mensagem longa não pode
 *  empurrar o histórico da conversa para fora da tela. */
export const ALTURA_MAX_CAIXA = 128;

/** Altura da caixa para um dado `scrollHeight`, presa entre piso e teto.
 *
 * Separada da componente porque é a regra que importa e o jsdom não calcula
 * layout — `scrollHeight` lá é sempre 0, então testar só pelo DOM não provaria
 * nem o piso nem o teto. */
export function alturaAutomatica(
  scrollHeight: number,
  min: number = ALTURA_MIN_CAIXA,
  max: number = ALTURA_MAX_CAIXA
): number {
  return Math.min(Math.max(scrollHeight, min), max);
}

interface CustomerChatComposerProps {
  sessionId: string;
  disabled?: boolean;
  /** Cresce a cada clique na conversa — inclusive na já aberta — e leva o cursor à caixa. */
  pedidoDeFoco?: number;
}

export function CustomerChatComposer({
  sessionId,
  disabled = false,
  pedidoDeFoco,
}: CustomerChatComposerProps) {
  const [draft, setDraft] = useState("");
  // O ✕ que a pessoa deu no cartão do site (o link NORMALIZADO): vale enquanto o primeiro link do rascunho for esse.
  // Anda junto com o rascunho: zera em todo caminho que o limpa (o envio e o efeito de `disabled`).
  const [semCartaoDe, setSemCartaoDe] = useState<string | null>(null);
  const [attachError, setAttachError] = useState<string | null>(null);
  // imagem escolhida aguardando marcação no editor (antes de enviar).
  const [editorFile, setEditorFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const sendMutation = useSendPortalMessage(sessionId);

  // Nota de voz (A4): status do consent + grant + estados do gravador/modal.
  const consentQuery = useVoiceConsentStatus();
  const grantMutation = useGrantVoiceConsent();
  // fail-closed enquanto o status carrega (mostra o gate, não o gravador).
  const granted = consentQuery.data?.granted ?? false;
  const [voiceActive, setVoiceActive] = useState(false);
  const [uploadingVoice, setUploadingVoice] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [videoActive, setVideoActive] = useState(false);
  const [menuAnexos, setMenuAnexos] = useState(false);
  /** Pedido de gravar vídeo vindo do menu — o gravador é montado só então.
   *  Antes ele ocupava um botão permanente na barra; agora entra sob demanda,
   *  que é o que devolve espaço para a caixa de texto. */
  const [pedirVideo, setPedirVideo] = useState(false);
  const [consentModalOpen, setConsentModalOpen] = useState(false);

  /** Com texto digitado, o botão da direita vira ENVIAR; vazio, vira microfone.
   *  É o padrão do WhatsApp e o que permite ter 2 botões no lugar de 4. */
  const temTexto = draft.trim().length > 0;

  /** Cresce com o texto, como no WhatsApp — até o teto, e aí rola por dentro.
   *
   *  Precisa de JS: um `<textarea rows={1}>` NÃO cresce sozinho. A altura vem
   *  do atributo `rows`, então `max-h-32` sozinho nunca entra em ação — a caixa
   *  fica de uma linha e rola internamente. Eu havia escrito num comentário que
   *  ela "cresce até 4 linhas": era descrição da intenção, não do que o código
   *  fazia. */
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    // zerar antes de medir: sem isto o `scrollHeight` nunca diminui ao apagar
    // texto, porque a própria altura atual passa a ser o piso da medição.
    el.style.height = "auto";
    el.style.height = `${alturaAutomatica(el.scrollHeight)}px`;
  }, [draft, voiceActive, videoActive]);

  const isBusy = sendMutation.isPending;
  const isDisabled = disabled || isBusy || uploadingVoice || uploadingVideo;

  // No computador, o cursor na caixa ao abrir a conversa e de volta depois de
  // cada envio (a caixa trava enquanto envia). No celular, não: o teclado
  // cobriria a conversa. Pedido do Augusto, 14/10/2026.
  useFocoNaCaixa(textareaRef, {
    conversa: sessionId,
    desligado: isDisabled || voiceActive || videoActive,
    pedido: pedidoDeFoco,
  });

  // Patch A6: AC3 exige textarea VAZIO quando disabled=true. Sem isso,
  // se o parceiro tinha rascunho e a sessão fechou no meio, o draft
  // ficava preso atrás do placeholder "Sessão encerrada.".
  useEffect(() => {
    if (disabled) {
      setDraft("");
      setSemCartaoDe(null);
      setAttachError(null);
    }
  }, [disabled]);

  const handleSendText = async () => {
    const trimmed = draft.trim();
    if (!trimmed || isDisabled) return;
    // O ✕ dado no cartão vai junto, e só quando casa com o primeiro link do texto que SAI: chave AUSENTE sem ✕, nunca
    // `null` (no atendimento o servidor responde 422).
    const content: Record<string, unknown> = { type: "text", text: trimmed };
    const linkDispensado = linkDispensadoParaEnviar(primeiroLink(trimmed), semCartaoDe);
    if (linkDispensado !== undefined) content.link_dispensado = linkDispensado;
    try {
      await sendMutation.mutateAsync({ content });
      setDraft("");
      setSemCartaoDe(null);
    } catch {
      // Mantém draft pra usuário tentar de novo. Erro virá via
      // toast / interceptor global do portalApi.
    }
  };

  // Nota de voz (A4): gravou → sobe via `portalApi` (endpoint dedicado — o parceiro
  // não alcança o `/storage/upload` interno) → envia content de áudio (só
  // `storage_key`; a A1 valida `extra="forbid"` e re-assina a url no render). Se o
  // send vier 412 (consent revogado numa corrida), reabre o modal em vez de erro cru.
  const handleVoiceNoteSend = async (payload: VoiceRecorderPayload) => {
    if (isDisabled) return;
    const ext = payload.mime.includes("mp4")
      ? "mp4"
      : payload.mime.includes("mpeg")
        ? "mp3"
        : payload.mime.includes("ogg")
          ? "ogg"
          : "webm";
    const file = new File([payload.blob], `note.${ext}`, { type: payload.mime });
    setUploadingVoice(true);
    try {
      const uploaded = await portalAtendimentosApi.uploadVoiceNote(file);
      const content: Record<string, unknown> = {
        type: "audio",
        storage_key: uploaded.storage_key,
        duration_sec: payload.durationSec,
        mime: payload.mime,
        size: payload.sizeBytes,
      };
      if (payload.caption) content.caption = payload.caption;
      await sendMutation.mutateAsync({ content });
    } catch (err: any) {
      if (err?.response?.status === 412) {
        setConsentModalOpen(true); // consent ausente/revogado → fluxo de aceite
      } else {
        console.error("Nota de voz (portal) falhou:", err);
        toast.error(`Erro ao enviar nota de voz: ${err?.message || "desconhecido"}`);
      }
    } finally {
      setUploadingVoice(false);
    }
  };

  /** Envia o vídeo gravado: sobe ao storage e manda só a `storage_key`.
   *
   * Mesmo caminho do anexo de imagem — nada de base64. O `duration_sec` vai no
   * content para a bolha mostrar a duração sem ter que baixar o vídeo. */
  const handleVideoSend = async (payload: VideoRecorderPayload) => {
    if (isDisabled) return;
    const ext = payload.mime.includes("webm") ? "webm" : "mp4";
    const file = new File([payload.blob], `video.${ext}`, {
      // `payload.mime` vem com codecs (`video/mp4;codecs=avc1…`); o backend
      // casa pelo tipo base, então corta aqui em vez de deixá-lo lidar com isso.
      type: payload.mime.split(";")[0],
    });
    setUploadingVideo(true);
    setAttachError(null);
    try {
      const { storage_key } = await portalAtendimentosApi.uploadAttachment(file);

      // Miniatura: sobe como imagem comum, no mesmo módulo. É o que permite a
      // bolha mostrar algo reconhecível SEM baixar o vídeo — medido que o
      // `preload="metadata"` não impede o download (readyState 4 ao abrir).
      //
      // Falha aqui não impede o envio: sem miniatura a bolha cai num cartão
      // neutro, que é bem melhor que perder o vídeo.
      let poster_key: string | undefined;
      if (payload.poster) {
        try {
          const posterFile = new File([payload.poster], "poster.jpg", { type: "image/jpeg" });
          poster_key = (await portalAtendimentosApi.uploadAttachment(posterFile)).storage_key;
        } catch {
          /* segue sem miniatura */
        }
      }

      await sendMutation.mutateAsync({
        content: {
          type: "video",
          filename: file.name,
          mime: file.type,
          size: file.size,
          duration_sec: payload.durationSec,
          storage_key,
          ...(poster_key ? { poster_key } : {}),
        },
      });
    } catch (err: any) {
      const status = err?.response?.status;
      setAttachError(
        status === 413
          ? "O vídeo ficou grande demais para envio. Grave um trecho mais curto."
          : "Não foi possível enviar o vídeo. Tente novamente."
      );
    } finally {
      setUploadingVideo(false);
    }
  };

  const handleAcceptConsent = () => {
    grantMutation.mutate(undefined, {
      onSuccess: () => setConsentModalOpen(false),
    });
  };

  const handleAttachClick = () => {
    if (isDisabled) return;
    setAttachError(null);
    fileInputRef.current?.click();
  };

  /**
   * Reduz imagem antes do envio.
   *
   * **Por que.** O Augusto reportou (06/08/2026) que as imagens do chat
   * carregam muito devagar no celular. Duas causas somadas: o anexo viaja em
   * BASE64 dentro da mensagem (infla ~33% e vem no mesmo JSON da lista, sem
   * cache por imagem nem carregamento sob demanda) e nada era comprimido. Uma
   * foto de celular sai com 3–5 MB.
   *
   * E não era só lentidão: o teto inline é 1 MB, então a foto do celular
   * simplesmente **não passava** — o parceiro recebia erro e ficava sem
   * conseguir anexar. Comprimir devolve a funcionalidade, não só a velocidade.
   *
   * Reusa o `compressImage` do fluxo da câmera do Lotese (~300 KB, 1920px), que
   * já resolve a armadilha do CSP: no modo web worker a lib busca script no CDN
   * jsdelivr, que `script-src 'self'` bloqueia.
   *
   * **Import dinâmico de propósito**: este compositor está no grafo ansioso
   * (App → AtendimentoPage → …), e trazer a lib de compressão estaticamente
   * engordaria o boot de todo usuário — inclusive quem nunca anexa nada.
   */
  const comprimirSePossivel = async (file: File): Promise<File> => {
    if (!file.type.startsWith("image/") || file.type === "image/svg+xml") return file;
    try {
      const { compressImage } = await import("@/components/lotese/imageUtils");
      // PRAZO LIMITE — não é zelo excessivo, é a diferença entre falhar e
      // TRAVAR. A compressão decodifica via `canvas`/`Image`; se a decodificação
      // nunca completa (arquivo corrompido, formato que o aparelho não abre), a
      // promessa fica pendurada para sempre. `try/catch` não cobre isso: pega
      // exceção, não travamento. Sem o prazo, o sintoma seria o pior possível —
      // o parceiro anexa, o botão gira, e a mensagem nunca sai nem dá erro.
      const blob = await Promise.race([
        compressImage(file),
        new Promise<null>((r) => setTimeout(() => r(null), TEMPO_MAX_COMPRESSAO_MS)),
      ]);
      if (!blob) return file; // estourou o prazo → segue com o original
      // Só troca se de fato encolheu — comprimir pode crescer arquivo já
      // otimizado, e aí o certo é ficar com o original.
      if (blob.size >= file.size) return file;
      const nome = file.name.replace(/\.(png|webp|heic|heif)$/i, ".jpg");
      return new File([blob], nome, { type: "image/jpeg" });
    } catch {
      // Falha de compressão não pode impedir o envio: segue com o original e
      // deixa o teto de 1 MB abaixo decidir. Silenciar aqui é deliberado — o
      // parceiro não tem o que fazer com "a compressão falhou".
      return file;
    }
  };

  // Envia UM anexo inline (base64). Reusado pelo caminho direto e pelo editor de
  // imagem. Re-checa o teto de 1 MB (o flatten do editor pode crescer).
  const sendAttachment = async (original: File) => {
    const file = await comprimirSePossivel(original);
    // O teto de 1 MB era do JSONB (o arquivo ia embutido na mensagem). Com
    // storage ele deixou de existir: vale o do endpoint, 25 MB, conferido no
    // servidor. Era este limite que fazia a foto do celular ser recusada.
    if (file.size > MAX_ANEXO_BYTES) {
      setAttachError("Arquivo maior que 25 MB. Envie um arquivo menor.");
      return;
    }
    try {
      // STORAGE, não mais base64 inline (06/08/2026).
      //
      // O `attachment_b64` embutia o arquivo no JSON da mensagem: inflava ~33%,
      // vinha junto com a lista inteira (sem cache por imagem, sem carregamento
      // sob demanda) e forçava o teto de 1 MB que impedia foto de celular. O
      // lado do ATENDENTE já usava storage desde a #430 — eram dois lados da
      // mesma conversa com arquiteturas diferentes.
      //
      // A leitura é re-assinada no render pelo `resign_attachment_content`, que
      // fixa tenant E módulo: chave de outro tenant não recebe URL.
      const { storage_key } = await portalAtendimentosApi.uploadAttachment(file);
      const isImage = file.type.startsWith("image/");
      await sendMutation.mutateAsync({
        content: {
          type: isImage ? "image" : "file",
          filename: file.name,
          mime: file.type || "application/octet-stream",
          size: file.size,
          storage_key,
        },
      });
    } catch (err) {
      // Patch A5: distingue erro do FileReader vs erro do backend
      // ("backend rejeitou JSONB grande" / 422 etc).
      const isAxiosLike = typeof err === "object" && err !== null && "response" in err;
      if (isAxiosLike) {
        setAttachError(
          "O anexo não pôde ser enviado (servidor recusou). Tente um " +
            "arquivo menor ou aguarde upload dedicado em breve."
        );
      } else {
        setAttachError(err instanceof Error ? err.message : "Falha ao processar anexo.");
      }
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset pra permitir reanexar o mesmo arquivo após erro.
    if (e.target) e.target.value = "";
    if (!file) return;
    await recebeArquivo(file);
  };

  /**
   * O arquivo que chegou pelo 📎 OU pela colagem: os tetos de entrada e, se
   * for imagem raster, o editor de marcação antes do envio.
   *
   * @param abreEditor false quando vieram várias imagens de uma vez (colagem):
   *        vários editores em fila seria sequestro do gesto — a mesma regra do
   *        chat interno. O portal não tem cartão de preparo, então elas seguem.
   */
  const recebeArquivo = async (file: File, abreEditor = true) => {
    // Patch E4: 0 bytes vira payload `attachment_b64: ""` no JSONB —
    // não é "anexo", é noise. Rejeita explicitamente.
    if (file.size === 0) {
      setAttachError("Arquivo vazio (0 bytes) não pode ser enviado.");
      return;
    }

    // Teto de ENTRADA. Para imagem ele é folgado de propósito: a compressão
    // (em `sendAttachment`) reduz para ~300 KB, e o teto real de 1 MB é
    // conferido DEPOIS, sobre o arquivo já reduzido.
    //
    // Antes, a checagem de 1 MB acontecia aqui, ANTES de qualquer compressão —
    // e como foto de celular tem 3–5 MB, o parceiro simplesmente não conseguia
    // anexar foto nenhuma: recebia "arquivo muito grande" e parava. Era o teto
    // rejeitando justamente o caso de uso principal.
    if (file.size > MAX_ANEXO_BYTES) {
      setAttachError(
        file.type.startsWith("image/")
          ? "Imagem grande demais (máx 25 MB). Tire a foto de novo com qualidade menor."
          : "Arquivo maior que 25 MB. Envie um arquivo menor."
      );
      return;
    }

    // Imagem raster (não SVG — viraria raster borrado) → abre o editor de marcação
    // ANTES de enviar. PDF/arquivo/SVG seguem o fluxo direto.
    if (abreEditor && file.type.startsWith("image/") && file.type !== "image/svg+xml") {
      setAttachError(null);
      if (!editorFile) setEditorFile(file);
      return;
    }

    await sendAttachment(file);
  };

  // Colar imagem (pedido do Augusto, 13/10/2026): a mesma porta do 📎.
  const aoColar = useColagemDeArquivos(async (arquivos) => {
    for (const f of arquivos) await recebeArquivo(f, arquivos.length === 1);
  }, isDisabled);

  // Editor "Enviar": a imagem JÁ rasterizada (marcações) vai como anexo inline.
  const handleEditorSend = async (edited: File) => {
    // O flatten do editor é na resolução ORIGINAL e pode crescer. Se passou do
    // teto, NÃO fecha o editor (senão o usuário acha que enviou e perde as
    // marcações) — avisa e deixa recortar uma área menor.
    //
    // O teto agora é o do storage (25 MB), não mais o de 1 MB do JSONB. E a
    // compressão em `sendAttachment` ainda reduz o que passar por aqui.
    if (edited.size > MAX_ANEXO_BYTES) {
      toast.error(
        "A imagem editada ficou grande demais (máx 25 MB). Recorte uma área menor e tente de novo."
      );
      return;
    }
    setEditorFile(null);
    await sendAttachment(edited);
  };

  return (
    // `sticky bottom-0` + fundo opaco: o Augusto pediu "fixar a parte que manda
    // mensagem" — ela rolava junto com o histórico e sumia da tela no celular,
    // obrigando a rolar até o fim para escrever.
    //
    // O padding de baixo soma a área segura com PISO literal, e não só o
    // `env()`: no Android o inset devolve 0, e foi exatamente por depender dele
    // sozinho que o botão da câmera continuou cortado no aparelho do Augusto
    // (Poco X7 Pro) depois do primeiro conserto. O piso protege onde o inset
    // não existe.
    <div
      className="sticky bottom-0 z-10 border-t bg-white p-3 space-y-2"
      style={{ paddingBottom: "max(0.75rem, calc(0.75rem + env(safe-area-inset-bottom, 0px)))" }}
      data-testid="portal-composer"
    >
      {attachError && (
        <div
          role="alert"
          className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-2 py-1"
        >
          {attachError}
        </div>
      )}
      {/* O cartão do site (prévia de link) em cima do campo: a 2ª linha da pilha, depois do erro de anexo e antes dos
          botões. O `mb-0` tira a margem própria da barra (a pilha já tem `space-y-2`). Some enquanto grava voz ou vídeo
          (o campo some também), fica travada com o campo (envio, anexo subindo) e o `key` amarra a barra à conversa. */}
      <BarraDoCartao
        key={sessionId}
        texto={draft}
        origem="portal"
        className="mb-0"
        escondida={voiceActive || videoActive}
        semCartaoDe={semCartaoDe}
        aoTirar={setSemCartaoDe}
        desabilitada={isDisabled}
      />
      <div className="flex items-end gap-2">
        {/* Nota de voz (A4): gravando/preview → esconde anexo/textarea/enviar
            e o gravador ocupa a barra (overlap #419). */}
        {!voiceActive && !videoActive && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              aria-hidden="true"
              tabIndex={-1}
              onChange={handleFileChange}
              data-testid="portal-attach-input"
            />
            {/* UM botão para todos os anexos, em vez de três lado a lado.

                O Augusto: "a caixa de texto fica muito pequenininha… ruim para
                a pessoa digitar". Medido no navegador a 412px de largura:

                                    antes        depois
                    caixa de texto  146px (35%)  242px (59%)
                    botões na barra  4            2

                O WhatsApp resolve assim: anexos atrás de um botão só, e o botão
                da direita ALTERNA entre microfone e enviar (nunca os dois). */}
            <Popover open={menuAnexos} onOpenChange={setMenuAnexos}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  className="h-12 w-12 shrink-0 rounded-full"
                  disabled={isDisabled}
                  aria-label="Anexar"
                  data-testid="portal-anexar"
                >
                  <Plus className="h-6 w-6" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" side="top" className="w-52 p-1.5">
                <button
                  type="button"
                  className="flex w-full items-center gap-3 rounded-md px-3 py-3 text-sm hover:bg-muted"
                  onClick={() => {
                    setMenuAnexos(false);
                    handleAttachClick();
                  }}
                  data-testid="portal-menu-arquivo"
                >
                  <Paperclip className="h-5 w-5 text-muted-foreground" />
                  Foto ou arquivo
                </button>
                <button
                  type="button"
                  className="flex w-full items-center gap-3 rounded-md px-3 py-3 text-sm hover:bg-muted"
                  onClick={() => {
                    setMenuAnexos(false);
                    setPedirVideo(true);
                  }}
                  data-testid="portal-menu-video"
                >
                  <VideoIcon className="h-5 w-5 text-muted-foreground" />
                  Gravar vídeo
                </button>
              </PopoverContent>
            </Popover>
            <Textarea
              ref={textareaRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  void handleSendText();
                }
              }}
              onPaste={aoColar}
              placeholder={disabled ? "Sessão encerrada." : "Digite uma mensagem"}
              title="Enter envia · Shift+Enter quebra linha"
              disabled={isDisabled}
              rows={1}
              // `flex-1` + `min-w-0`: sem o `min-w-0`, um filho de flex não
              // encolhe abaixo do conteúdo e volta a espremer a caixa.
              // A altura é ajustada por JS (ver `alturaAutomatica`); as classes
              // de altura aqui são o estado inicial, antes do primeiro efeito.
              className="min-h-[48px] max-h-32 min-w-0 flex-1 resize-none rounded-2xl py-3"
            />
          </>
        )}
        {/* MICROFONE ou ENVIAR — nunca os dois. É o padrão do WhatsApp, e o que
            permite 2 botões visíveis no lugar de 4. Com a barra cheia a caixa
            de texto ficava com 146px (35% da tela de 412px); com dois botões
            ela quase dobra.

            Sem consent LGPD o mic abre o modal de aceite; com consent, monta o
            gravador. */}
        {!temTexto &&
          !videoActive &&
          (granted ? (
            <VoiceRecorder
              onSend={handleVoiceNoteSend}
              onActiveChange={setVoiceActive}
              disabled={isDisabled}
              className={voiceActive ? "flex-1" : undefined}
            />
          ) : (
            <Button
              type="button"
              variant="ghost"
              className="h-12 w-12 shrink-0 rounded-full"
              onClick={() => setConsentModalOpen(true)}
              disabled={isDisabled}
              aria-label="Gravar nota de voz"
              data-testid="portal-gravar-voz"
            >
              <Mic className="h-6 w-6" />
            </Button>
          ))}
        {/* Gravador de VÍDEO — montado SOB DEMANDA, pelo menu de anexos.
            Antes ocupava um botão permanente na barra; era um dos quatro que
            espremiam a caixa de texto para 35% da tela. */}
        {!voiceActive && pedirVideo && (
          <VideoRecorder
            onSend={async (p) => {
              await handleVideoSend(p);
              setPedirVideo(false);
            }}
            onActiveChange={setVideoActive}
            onAviso={(m) => setAttachError(m)}
            enviando={uploadingVideo}
            disabled={isDisabled}
            className={videoActive ? "flex-1" : "h-12 w-12 shrink-0"}
            iniciarAoMontar
          />
        )}
        {temTexto && !voiceActive && !videoActive && (
          <Button
            type="button"
            className="h-12 w-12 shrink-0 rounded-full"
            onClick={() => void handleSendText()}
            disabled={isDisabled}
            aria-label="Enviar mensagem"
            data-testid="portal-enviar"
          >
            {isBusy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
          </Button>
        )}
      </div>
      <VoiceConsentModal
        open={consentModalOpen}
        onAccept={handleAcceptConsent}
        onCancel={() => setConsentModalOpen(false)}
        isGranting={grantMutation.isPending}
        isError={grantMutation.isError}
      />

      {editorFile && (
        <Suspense fallback={null}>
          <ImageMarkupEditor
            file={editorFile}
            open
            onSend={handleEditorSend}
            onCancel={() => setEditorFile(null)}
          />
        </Suspense>
      )}
    </div>
  );
}

export default CustomerChatComposer;
